import apiClient from './apiClient'

export type AdminRegistrationItem = {
  id: number
  code: string
  registerAt: string
  approvedAt?: string | null
  status: string
  fee: number
  originalFee?: number
  discountAmount?: number
  isGift?: boolean
  parentRegisterId?: number | null
  parentRegisterCode?: string | null
  parentCourseName?: string | null
  misaInvoiceStatus?: string | null
  misaInvNo?: string | null
  misaInvCode?: string | null
  enterpriseId?: number | null
  user: { fullName: string; email: string; phone?: string }
  course: { name: string; code: string; method?: string }
  classroom: { name: string; code: string }
  enterpriseName?: string | null
}

export type Pagination = {
  page: number
  limit: number
  total: number
  totalPages: number
}

export type AdminRegistrationsResponse = {
  success: boolean
  message: string
  data: { items: AdminRegistrationItem[]; pagination: Pagination }
}

export type AdminRegistrationFilter = {
  registration_scope?: 'all' | 'enterprise' | 'individual'
  code?: string
  email?: string
  phone?: string
  full_name?: string
  course?: string
  classroom?: string
  course_id?: number
  classroom_id?: number
  status?: string
  is_gift?: boolean | string
  page?: number
  limit?: number
}

export type AdminCourseGiftRule = {
  id?: number
  giftCourseId: number
  giftClassroomId?: number | null
  isActive?: boolean
  giftCourseName?: string
  giftCourseCode?: string
  giftClassroomName?: string
  giftClassroomCode?: string
}

export type AdminCourse = {
  id: number
  hashId?: string
  code: string
  name: string
  status?: string
  is_active?: number
  isActive?: number
  fee?: number
  method?: string
  shortDescription?: string
  requiredInput?: string
  detailIntroduce?: string
  rule?: string
  suitableObject?: string
  content?: string
  lecturer?: string
  timeInHour?: number
  timeInLesson?: number | null
  totalRegisteredStudent?: number
  imageUrl?: string | null
  metaDescription?: string | null
  openGraphImageUrl?: string | null
  slug?: string | null
  tags?: string | null
  updated_at?: string
  updatedAt?: string
  createdAt?: string
  audienceIds?: number[]
  primaryAudienceId?: number | null
  audiences?: Array<{ id: number; code: string; name: string; isPrimary: boolean }>
  contentFromCourseId?: number | null
  landingSections?: {
    heroHeadline?: string
    heroSubheadline?: string
    painPoints?: string[]
    outcomes?: { title: string; detail: string }[]
    beforeAfter?: { task: string; before: string; after: string; saving: string }[]
    demoUrls?: string[]
    faqs?: { q: string; a: string }[]
    instructor?: {
      imageUrl?: string
      imageOverlay?: string
      sectionLabel?: string
      name?: string
      bio?: string
      stats?: { value: string; label: string }[]
    }
  } | null
}

export type AdminAudience = {
  id: number
  code: string
  name: string
  shortQuestion?: string | null
  description?: string | null
  sortOrder: number
  isActive: boolean
}

export type AdminCourseUpsertPayload = {
  code?: string | null
  name: string
  method: string
  fee: number
  status: string
  isActive?: number
  timeInHour?: number
  timeInLesson?: number | null
  shortDescription?: string | null
  requiredInput?: string | null
  detailIntroduce?: string | null
  rule?: string | null
  suitableObject?: string | null
  content?: string | null
  lecturer?: string | null
  imageUrl?: string | null
  slug?: string | null
  metaDescription?: string | null
  openGraphImageUrl?: string | null
  tags?: string | null
  audienceIds?: number[]
  primaryAudienceId?: number | null
  landingSections?: AdminCourse['landingSections']
}

export type AdminClassroom = {
  id: number
  course_id: number
  code: string
  name: string
  fee: number
  start_date?: string
  end_date?: string
  status?: string
  course_name: string
  course_code: string
}

export type AdminCourseComment = {
  id: number
  courseContentId: number
  userId: number
  userName: string
  commentText: string
  createdAt: string
  contentName: string
  contentType: string
  replyCount: number
  isAnswered: boolean
}

export type AdminCourseCommentsResponse = {
  success: boolean
  data: {
    items: AdminCourseComment[]
    summary: { all: number; answered: number; unanswered: number }
  }
}

export type AdminCourseContent = {
  id: number
  hashId?: string
  courseId: number
  contentType: 'VIDEO' | 'POST' | 'FORDER' | 'TEST'
  name: string
  detail?: string
  objectId?: number | null
  parentId?: number | null
  position?: number
  isPublic?: boolean
  status?: string
  createdAt?: string
  updatedAt?: string
}

export type AdminVideo = {
  id: number
  hashId?: string
  fileName?: string
  fileType?: string
  title: string
  path?: string
  key?: string
  duration?: number
  description?: string
  createdAt?: string
  isActive?: number
  fileSize?: number
  fileExists?: boolean
  usageCount?: number
}

export type AdminVideoUsage = {
  contentId: number
  contentName: string
  courseId: number
  courseName: string
  courseHashId?: string | null
  parentId?: number | null
  position?: number | null
}

/** Max video size client-side (1.5 GB) — must match backend */
export const ADMIN_VIDEO_MAX_BYTES = Math.floor(1.5 * 1024 * 1024 * 1024)
/** Chunk size for resumable upload (~8 MB) */
export const ADMIN_VIDEO_CHUNK_BYTES = 8 * 1024 * 1024

export type AdminVideoUploadProgress = {
  phase: 'init' | 'uploading' | 'completing' | 'done'
  percent: number
  uploadedBytes: number
  totalBytes: number
  chunkIndex?: number
  totalChunks?: number
}

export type AdminTestPaper = {
  id: number
  hashId?: string
  name: string
  description?: string
  minute?: number
  isActive?: number
  isPublic?: number
  totalQuestion?: number
  objectId?: number | null
  maxTime?: number
  passPercent?: number
  createdAt?: string
  /** Số skill gắn cấp paper (Ask AI) */
  skillLinkCount?: number
}

export type AdminTestObject = {
  id: number
  name: string
  description?: string
  isActive?: number
}

export type AdminQuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'ESSAY' | 'CHOICE'
export type AdminQuestionLevel = 'EASY' | 'MEDIUM' | 'HARD'

export type AdminQuestionTopic = {
  id: number
  name: string
  description?: string | null
  parentId?: number | null
  path?: string | null
  isActive?: number | null
  createdAt?: string | null
  updatedAt?: string | null
}

export type AdminQuestionGroup = {
  id: number
  name: string
  isActive?: number | null
  preview?: string
  questionCount?: number
  questions?: AdminQuestionListItem[]
  createdAt?: string | null
  updatedAt?: string | null
}

export type AdminQuestionAnswer = {
  id?: number
  questionId?: number
  content: string
  isCorrect?: number | boolean | null
  updatedAt?: string | null
}

export type AdminQuestionListItem = {
  id: number
  content: string
  preview?: string
  topicId?: number | null
  parentTopicId?: number | null
  point?: number | null
  level: string
  questionType: string
  essayAnswerType?: string | null
  essayAnswer?: string | null
  groupId?: number | null
  sortGroup?: number | null
  isActive: number
  creatorId?: number
  helpContent?: string | null
  createdAt?: string
  updatedAt?: string
  answersCount?: number
  answers?: AdminQuestionAnswer[]
  group?: AdminQuestionGroup | null
  topicName?: string | null
}

export type AdminQuestionUpsertPayload = {
  content: string
  questionType: AdminQuestionType
  level?: AdminQuestionLevel
  point?: number | null
  topicId?: number | null
  parentTopicId?: number | null
  groupId?: number | null
  sortGroup?: number | null
  helpContent?: string | null
  essayAnswerType?: string | null
  essayAnswer?: string | null
  isActive?: number
  answers?: Array<{ id?: number; content: string; isCorrect?: boolean | number }>
}

export type AdminTestPaperUpsertPayload = {
  name: string
  description?: string | null
  minute?: number
  maxTime?: number
  passPercent?: number | null
  objectId?: number | null
  isActive?: number
  isPublic?: number
  needApproveResult?: number | null
  expiredDay?: number | null
  allowViewAnswerType?: string | null
  viewAnswerConfigValue?: number | null
  allowPause?: number | null
  image?: string | null
}

export type AdminTestPaperQuestionInput = {
  questionId: number
  point?: number
  sort?: number
  groupId?: number | null
}

export type AdminMisaInvoiceLog = {
  id: number
  scope: string
  enterprise_registration_batch_id?: number | null
  enterprise_id?: number | null
  register_ids: number[] | string
  invoice_amount_total: number
  invoice_date: string
  status: 'PENDING' | 'SUCCESS' | 'FAIL'
  misa_inv_no?: string | null
  misa_inv_code?: string | null
  misa_transaction_id?: string | null
  misa_invoice_ref?: string | null
  error_message?: string | null
  response_excerpt?: string | null
  attempt_no: number
  created_at: string
  updated_at: string
  enterprise_name?: string | null
  tax_code?: string | null
}

export type AdminEnterpriseBatchItem = {
  id: number
  enterpriseId: number
  taxCode: string
  enterpriseName: string
  invoiceEmail: string
  address: string
  createdByUserId: number
  createdByName?: string
  createdAt: string
  totalRegistrations: number
  giftCount: number
  successCount: number
  totalAmount: number
  invoiceStatus: 'CHUA_XUAT' | 'SUCCESS' | 'FAIL'
  misaInvNo?: string | null
  misaInvoiceDate?: string | null
  misaError?: string | null
}

export type AdminEnterpriseBatchDetail = {
  batch: {
    id: number
    enterpriseId: number
    taxCode: string
    enterpriseName: string
    invoiceEmail: string
    address: string
    createdAt: string
    createdByName?: string
  }
  registrations: Array<{
    id: number
    code: string
    userId: number
    courseId: number
    classroomId: number
    fee: number
    orgFee: number
    discountAmount: number
    status: string
    registerAt: string
    approvedAt?: string
    isGift: boolean
    misaInvoiceStatus?: string
    misaInvNo?: string
    misaInvDate?: string
    studentName: string
    studentEmail: string
    studentPhone?: string
    courseName: string
    courseCode: string
    classroomName: string
    classroomCode: string
  }>
}

type ApiResult = { success: boolean; message: string; data: unknown }

export type AdminSurveyItem = {
  id: number
  studentId: number
  rate: number
  note?: string | null
  status: string
  courseId: number
  classroomId: number
  userId: number
  createdAt: string
  updatedAt: string
  approvedAt?: string | null
  approvedBy?: number | null
  approvedNote?: string | null
  isHidden: boolean
  user: { fullName: string; email: string; phone?: string }
  course: { id: number; code: string; name: string }
  classroom: { id: number; code: string; name: string }
}

export type AdminSurveyFilter = {
  course_id?: number
  status?: 'SUBMITTED' | 'HIDDEN'
  page?: number
  limit?: number
}

export type AdminSurveysResponse = {
  success: boolean
  message: string
  data: { items: AdminSurveyItem[]; pagination: Pagination }
}

export type AdminTestResultGradingStatus = 'pending' | 'graded' | 'auto'

export type AdminTestResultListItem = {
  id: number
  paperId: number
  paperName: string
  courseId: number | null
  courseName: string
  userId: number
  fullName: string
  email: string
  phone: string | null
  zaloUrl: string | null
  point: number
  maxPoint: number
  percent: number
  totalQuestion: number
  testedSecond: number | null
  savedAt: string | null
  createdAt: string
  gradingStatus: AdminTestResultGradingStatus
  pendingEssayCount: number
  essayCount: number
}

export type AdminTestResultQuestion = {
  answerId: number
  sort: number
  questionId: number
  groupId: number | null
  maxPoint: number
  questionType: string
  questionContent: string
  selectedAnswerIds: number[]
  answerContent: string | null
  isCorrect: number | null
  point: number
  status: string
  mentorComment: string | null
  mentorId: number | null
  approvedAt: string | null
  answers: Array<{ id: number; content: string | null; isCorrect: boolean }>
  essayAnswer: string | null
  helpContent: string | null
}

export type AdminTestResultDetail = {
  id: number
  paperId: number
  paperName: string
  courseId: number | null
  courseName: string
  point: number
  maxPoint: number
  percent: number
  totalQuestion: number
  testedSecond: number | null
  savedAt: string | null
  createdAt: string
  gradingStatus: AdminTestResultGradingStatus
  hasEssay: boolean
  pendingCount: number
  correctCount: number
  wrongCount: number
  student: {
    userId: number
    fullName: string
    email: string
    phone: string | null
    zaloUrl: string | null
  }
  groups: Array<{ id: number; name: string }>
  questions: AdminTestResultQuestion[]
}

export type AdminTestResultListResponse = {
  success: boolean
  data: { items: AdminTestResultListItem[]; pagination: Pagination }
}

export type AdminTestResultGradePayload = {
  grades: Array<{ answerId: number; point: number; mentorComment?: string }>
}

/** Ràng buộc đủ method — tránh CI/build thiếu bản cập nhật object. */
export type AdminApiService = {
  getRegistrations(filters: AdminRegistrationFilter): Promise<AdminRegistrationsResponse>
  getClassrooms(courseId?: number): Promise<{ success: boolean; data: { items: AdminClassroom[] } }>
  getCourses(): Promise<{ success: boolean; data: { items: AdminCourse[] } }>
  getCourseById(courseId: number): Promise<{ success: boolean; data: AdminCourse }>
  getCourseGiftRules(courseId: number): Promise<{ success: boolean; data: { items: AdminCourseGiftRule[] } }>
  saveCourseGiftRules(
    courseId: number,
    rules: Array<{ giftCourseId: number; giftClassroomId?: number | null; isActive?: boolean }>
  ): Promise<{ success: boolean; data: { items: AdminCourseGiftRule[] }; message?: string }>
  getAudiences(): Promise<{ success: boolean; data: { items: AdminAudience[] } }>
  createCourse(data: AdminCourseUpsertPayload): Promise<{ success: boolean; data: AdminCourse; message?: string }>
  updateCourse(courseId: number, data: AdminCourseUpsertPayload): Promise<{ success: boolean; data: AdminCourse; message?: string }>
  getCourseContent(courseId: number): Promise<{
    success: boolean
    data: AdminCourseContent[]
    contentCourseId?: number
    contentFromCourseId?: number | null
  }>
  getCourseContentItem(contentId: number): Promise<{ success: boolean; data: AdminCourseContent }>
  getCourseComments(
    courseId: number,
    status?: 'all' | 'answered' | 'unanswered'
  ): Promise<AdminCourseCommentsResponse>
  getAdminTestDetails(paperId: number): Promise<{ success: boolean; data: any }>
  createCourseContent(courseId: number, data: Partial<AdminCourseContent>): Promise<ApiResult>
  copyCourseContentFrom(
    courseId: number,
    data: {
      sourceCourseId: number
      targetParentId: number | null
      itemIds: number[]
    }
  ): Promise<{
    success: boolean
    message?: string
    data?: { createdCount: number; items: Array<{ id: number; sourceId: number; name: string }> }
  }>
  updateCourseContent(contentId: number, data: Partial<AdminCourseContent>): Promise<ApiResult>
  deleteCourseContent(contentId: number): Promise<ApiResult>
  reorderCourseContent(
    courseId: number,
    orderedIds: number[],
    parentId: number | null
  ): Promise<ApiResult>
  listVideos(params?: {
    q?: string
    page?: number
    limit?: number
    includeInactive?: boolean
  }): Promise<{
    success: boolean
    data: {
      items: AdminVideo[]
      pagination: Pagination
      limits?: { maxBytes: number; maxChunkBytes: number }
    }
  }>
  getVideo(id: number): Promise<{ success: boolean; data: AdminVideo; message?: string }>
  getVideoUsages(id: number): Promise<{
    success: boolean
    data: {
      videoId: number
      videoTitle: string
      usageCount: number
      usages: AdminVideoUsage[]
    }
    message?: string
  }>
  updateVideo(
    id: number,
    data: { title?: string; description?: string | null; duration?: number; isActive?: number }
  ): Promise<{ success: boolean; data: AdminVideo; message?: string }>
  deleteVideo(
    id: number,
    options?: { hard?: boolean }
  ): Promise<{
    success: boolean
    message?: string
    data?: { usageCount?: number; usages?: AdminVideoUsage[] }
  }>
  uploadVideo(file: File, title?: string): Promise<{ success: boolean; data: AdminVideo; message?: string }>
  uploadVideoChunked(
    file: File,
    options?: {
      title?: string
      description?: string
      duration?: number
      onProgress?: (progress: AdminVideoUploadProgress) => void
      signal?: AbortSignal
    }
  ): Promise<{ success: boolean; data: AdminVideo; message?: string }>
  listTestPapers(params?: {
    q?: string
    objectId?: number
    isActive?: number
    page?: number
    limit?: number
  }): Promise<{
    success: boolean
    data: { items: AdminTestPaper[]; pagination: Pagination }
  }>
  listTestObjects(): Promise<{ success: boolean; data: AdminTestObject[] }>
  listQuestionTopics(): Promise<{ success: boolean; data: AdminQuestionTopic[] }>
  createQuestionTopic(payload: {
    name: string
    description?: string | null
    parentId?: number | null
    isActive?: number
  }): Promise<{ success: boolean; data: AdminQuestionTopic; message?: string }>
  updateQuestionTopic(
    id: number,
    payload: {
      name?: string
      description?: string | null
      parentId?: number | null
      isActive?: number
    }
  ): Promise<{ success: boolean; data: AdminQuestionTopic; message?: string }>
  listQuestionGroups(params?: {
    q?: string
    isActive?: number
    page?: number
    limit?: number
  }): Promise<{
    success: boolean
    data: { items: AdminQuestionGroup[]; pagination: Pagination }
  }>
  getQuestionGroup(id: number): Promise<{ success: boolean; data: AdminQuestionGroup }>
  createQuestionGroup(payload: {
    name: string
    isActive?: number
  }): Promise<{ success: boolean; data: AdminQuestionGroup; message?: string }>
  updateQuestionGroup(
    id: number,
    payload: { name?: string; isActive?: number }
  ): Promise<{ success: boolean; data: AdminQuestionGroup; message?: string }>
  listQuestions(params?: {
    q?: string
    topicId?: number
    groupId?: number
    questionType?: string
    level?: string
    isActive?: number
    excludeIds?: number[]
    ungrouped?: boolean
    page?: number
    limit?: number
  }): Promise<{
    success: boolean
    data: { items: AdminQuestionListItem[]; pagination: Pagination }
  }>
  getQuestion(id: number): Promise<{ success: boolean; data: AdminQuestionListItem }>
  createQuestion(
    payload: AdminQuestionUpsertPayload
  ): Promise<{ success: boolean; data: AdminQuestionListItem; message?: string }>
  updateQuestion(
    id: number,
    payload: Partial<AdminQuestionUpsertPayload>
  ): Promise<{ success: boolean; data: AdminQuestionListItem; message?: string }>
  setQuestionActive(
    id: number,
    isActive: number
  ): Promise<{ success: boolean; data: AdminQuestionListItem; message?: string }>
  createTestPaper(
    payload: AdminTestPaperUpsertPayload
  ): Promise<{ success: boolean; data: AdminTestPaper; message?: string }>
  updateTestPaper(
    paperId: number,
    payload: Partial<AdminTestPaperUpsertPayload>
  ): Promise<{ success: boolean; data: AdminTestPaper; message?: string }>
  setTestPaperQuestions(
    paperId: number,
    questions: AdminTestPaperQuestionInput[]
  ): Promise<{
    success: boolean
    data: {
      paper: AdminTestPaper
      questions: Array<{
        id: number
        paperId: number
        questionId: number
        groupId?: number | null
        point: number
        sort: number
      }>
    }
    message?: string
  }>
  setTestPaperActive(
    paperId: number,
    isActive: number
  ): Promise<{ success: boolean; data: AdminTestPaper; message?: string }>
  uploadImage(file: File): Promise<{ success: boolean; data: { url: string }; message?: string }>
  createEnterpriseBatch(payload: {
    taxCode: string
    enterpriseName: string
    invoiceEmail: string
    address: string
    /** NOW = xuất HĐ MISA ngay; LATER = xuất khi admin bấm Xuất hóa đơn trên danh sách */
    invoiceMode?: 'NOW' | 'LATER'
    rows: Array<{ fullName: string; email: string; phone?: string; classroomId: number; fee?: number; sourceRowIndex?: number }>
  }): Promise<ApiResult>
  getEnterpriseBatches(params?: { page?: number; limit?: number; search?: string }): Promise<{
    success: boolean
    data: { items: AdminEnterpriseBatchItem[]; pagination: Pagination }
  }>
  getEnterpriseBatchDetail(batchId: number): Promise<{
    success: boolean
    data: AdminEnterpriseBatchDetail
  }>
  publishMisaForEnterpriseBatch(batchId: number): Promise<ApiResult>
  publishMisaForRegistration(registrationId: number): Promise<ApiResult>
  confirmRegistrationFulfillment(registrationId: number): Promise<ApiResult>
  cancelWaitPaymentRegistration(registrationId: number): Promise<ApiResult>
  getMisaLogs(params: { page?: number; limit?: number; status?: 'PENDING' | 'SUCCESS' | 'FAIL' }): Promise<{
    success: boolean
    data: { items: AdminMisaInvoiceLog[]; pagination: Pagination }
  }>
  retryMisaLog(logId: number): Promise<ApiResult>
  getSecurityWarnings(params?: { page?: number; limit?: number }): Promise<{
    success: boolean
    data: {
      items: AdminSecurityWarningItem[]
      pagination: Pagination
      unresolvedCount: number
    }
  }>
  getSecurityWarningDetail(userId: number): Promise<{
    success: boolean
    data: AdminSecurityWarningDetail
  }>
  listWatchlist(params?: { page?: number; limit?: number }): Promise<{
    success: boolean
    data: {
      items: AdminWatchlistItem[]
      pagination: Pagination
    }
  }>
  addToWatchlist(
    userId: number,
    payload?: { reason?: string; note?: string }
  ): Promise<ApiResult>
  removeFromWatchlist(userId: number): Promise<ApiResult>
  unlockUser(userId: number): Promise<ApiResult>
  lockUser(userId: number, reason?: string): Promise<ApiResult>
  listUsers(params?: AdminUserFilter): Promise<AdminUsersListResponse>
  getUser(userId: number): Promise<{ success: boolean; data: AdminUserItem; message?: string }>
  updateUser(userId: number, payload: AdminUserUpdatePayload): Promise<{
    success: boolean
    data: AdminUserItem
    message?: string
  }>
  revokeUserDevice(userId: number, deviceRowId: number): Promise<ApiResult>
  revokeAllUserDevices(userId: number): Promise<ApiResult>
  getSurveys(filters?: AdminSurveyFilter): Promise<AdminSurveysResponse>
  setSurveyVisibility(
    surveyId: number,
    hidden: boolean,
    note?: string
  ): Promise<ApiResult & { data?: { id: number; status: string; isHidden: boolean } }>
  listTestResults(filters?: {
    courseId?: number
    paperId?: number
    gradingStatus?: AdminTestResultGradingStatus
    q?: string
    page?: number
    limit?: number
  }): Promise<AdminTestResultListResponse>
  getTestResult(resultId: number): Promise<{ success: boolean; data: AdminTestResultDetail; message?: string }>
  gradeTestResult(
    resultId: number,
    payload: AdminTestResultGradePayload
  ): Promise<ApiResult & { data?: AdminTestResultDetail }>
  getBlogPostsMeta(): Promise<{
    success: boolean
    data: {
      topics: AdminBlogTopicOption[]
      sharingTypes: AdminBlogSharingTypeOption[]
      statuses: AdminBlogPostStatus[]
    }
  }>
  listBlogPosts(filters?: AdminBlogPostFilter): Promise<AdminBlogPostsListResponse>
  getBlogPost(id: number): Promise<{ success: boolean; data: AdminBlogPost; message?: string }>
  createBlogPost(
    payload: AdminBlogPostPayload
  ): Promise<{ success: boolean; data: AdminBlogPost; message?: string }>
  updateBlogPost(
    id: number,
    payload: Partial<AdminBlogPostPayload>
  ): Promise<{ success: boolean; data: AdminBlogPost; message?: string }>
  deleteBlogPost(id: number): Promise<ApiResult & { data?: { id: number } }>
  getWorkshopRegistrations(params: {
    page?: number
    limit?: number
    search?: string
    workshop_id?: string
  }): Promise<{
    success: boolean
    data: { items: AdminWorkshopRegistrationItem[]; pagination: Pagination }
  }>
  sendWorkshopMeetLink(params: {
    meet_link: string
    workshop_id?: string
    force?: boolean
    registration_ids?: number[]
  }): Promise<{ success: boolean; message: string; total?: number }>
  getWorkshopEmailLogs(workshopId?: string): Promise<WorkshopEmailLogsResponse>
  retryFailedWorkshopEmails(params: {
    workshop_id?: string
    log_ids?: number[]
  }): Promise<{ success: boolean; message: string; retried?: number }>
  getWorkshopConfig(workshopId: string): Promise<WorkshopConfigResponse>
  updateWorkshopConfig(
    workshopId: string,
    payload: { youtube_url?: string | null; meet_link?: string | null }
  ): Promise<{ success: boolean; message: string }>
}

// ── Workshop Registration ──────────────────────────────────────
export type AdminWorkshopRegistrationItem = {
  id: number
  workshop_id: string
  workshop_name: string
  full_name: string
  email: string
  phone: string
  role?: string | null
  registered_at: string
  email_status?: 'pending' | 'success' | 'failed' | null
  email_sent_at?: string | null
}

export type WorkshopEmailLogItem = {
  id: number
  registration_id: number
  email: string
  full_name: string
  status: 'pending' | 'success' | 'failed'
  error_message?: string | null
  sent_at?: string | null
  created_at: string
}

export type WorkshopEmailLogStats = {
  total: number
  success_count: number
  failed_count: number
  pending_count: number
}

export type WorkshopEmailLogsResponse = {
  success: boolean
  data: {
    logs: WorkshopEmailLogItem[]
    stats: WorkshopEmailLogStats
  }
}

export type WorkshopConfig = {
  workshop_id: string
  youtube_url: string | null
  meet_link: string | null
  updated_at?: string | null
}

export type WorkshopConfigResponse = {
  success: boolean
  data: WorkshopConfig
}

export const AdminApi: AdminApiService = {
  async getRegistrations(filters: AdminRegistrationFilter): Promise<AdminRegistrationsResponse> {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => {
      if (v == null || v === '') return
      if (Array.isArray(v)) {
        if (v.length > 0) params.set(k, v.join(','))
      } else {
        params.set(k, String(v))
      }
    })
    const response = await apiClient.get<AdminRegistrationsResponse>(`/admin/registrations?${params.toString()}`)
    return response.data
  },

  async getClassrooms(courseId?: number): Promise<{ success: boolean; data: { items: AdminClassroom[] } }> {
    const suffix = courseId ? `?course_id=${courseId}` : ''
    const response = await apiClient.get<{ success: boolean; data: { items: AdminClassroom[] } }>(`/admin/classrooms${suffix}`)
    return response.data
  },

  async getCourses(): Promise<{ success: boolean; data: { items: AdminCourse[] } }> {
    const response = await apiClient.get<{ success: boolean; data: { items: AdminCourse[] } }>('/admin/courses')
    return response.data
  },

  async getCourseById(courseId: number): Promise<{ success: boolean; data: AdminCourse }> {
    const response = await apiClient.get<{ success: boolean; data: AdminCourse }>(`/admin/courses/${courseId}`)
    return response.data
  },

  async getCourseGiftRules(courseId: number): Promise<{ success: boolean; data: { items: AdminCourseGiftRule[] } }> {
    const response = await apiClient.get<{ success: boolean; data: { items: AdminCourseGiftRule[] } }>(
      `/admin/courses/${courseId}/gift-rules`
    )
    return response.data
  },

  async saveCourseGiftRules(
    courseId: number,
    rules: Array<{ giftCourseId: number; giftClassroomId: number; isActive?: boolean }>
  ): Promise<{ success: boolean; data: { items: AdminCourseGiftRule[] }; message?: string }> {
    const response = await apiClient.put<{
      success: boolean
      data: { items: AdminCourseGiftRule[] }
      message?: string
    }>(`/admin/courses/${courseId}/gift-rules`, { rules })
    return response.data
  },

  async getAudiences(): Promise<{ success: boolean; data: { items: AdminAudience[] } }> {
    const response = await apiClient.get<{ success: boolean; data: { items: AdminAudience[] } }>('/admin/audiences')
    return response.data
  },

  async createCourse(data: AdminCourseUpsertPayload): Promise<{ success: boolean; data: AdminCourse; message?: string }> {
    const response = await apiClient.post<{ success: boolean; data: AdminCourse; message?: string }>(
      '/admin/courses',
      data,
      { timeout: 60000 }
    )
    return response.data
  },

  async updateCourse(
    courseId: number,
    data: AdminCourseUpsertPayload
  ): Promise<{ success: boolean; data: AdminCourse; message?: string }> {
    const response = await apiClient.put<{ success: boolean; data: AdminCourse; message?: string }>(
      `/admin/courses/${courseId}`,
      data,
      { timeout: 60000 }
    )
    return response.data
  },

  async getCourseContent(courseId: number): Promise<{
    success: boolean
    data: AdminCourseContent[]
    contentCourseId?: number
    contentFromCourseId?: number | null
  }> {
    const response = await apiClient.get<{
      success: boolean
      data: AdminCourseContent[]
      contentCourseId?: number
      contentFromCourseId?: number | null
    }>(`/admin/courses/${courseId}/content`, { timeout: 60000 })
    return response.data
  },

  async getCourseContentItem(contentId: number): Promise<{ success: boolean; data: AdminCourseContent }> {
    const response = await apiClient.get<{ success: boolean; data: AdminCourseContent }>(
      `/admin/courses/content/${contentId}`,
      { timeout: 60000 }
    )
    return response.data
  },

  async getCourseComments(
    courseId: number,
    status: 'all' | 'answered' | 'unanswered' = 'all'
  ): Promise<AdminCourseCommentsResponse> {
    const response = await apiClient.get<AdminCourseCommentsResponse>(
      `/admin/courses/${courseId}/comments`,
      { params: { status }, timeout: 60000 }
    )
    return response.data
  },

  async getAdminTestDetails(paperId: number): Promise<{ success: boolean; data: any }> {
    const response = await apiClient.get<{ success: boolean; data: any }>(`/admin/tests/${paperId}`)
    return response.data
  },

  async createCourseContent(courseId: number, data: Partial<AdminCourseContent>): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/courses/${courseId}/content`, data, {
      timeout: 60000,
    })
    return response.data
  },

  async copyCourseContentFrom(
    courseId: number,
    data: {
      sourceCourseId: number
      targetParentId: number | null
      itemIds: number[]
    }
  ): Promise<{
    success: boolean
    message?: string
    data?: { createdCount: number; items: Array<{ id: number; sourceId: number; name: string }> }
  }> {
    const response = await apiClient.post<{
      success: boolean
      message?: string
      data?: { createdCount: number; items: Array<{ id: number; sourceId: number; name: string }> }
    }>(`/admin/courses/${courseId}/content/copy-from`, data, { timeout: 120000 })
    return response.data
  },

  async updateCourseContent(contentId: number, data: Partial<AdminCourseContent>): Promise<ApiResult> {
    const response = await apiClient.put<ApiResult>(`/admin/courses/content/${contentId}`, data, {
      timeout: 60000,
    })
    return response.data
  },

  async deleteCourseContent(contentId: number): Promise<ApiResult> {
    const response = await apiClient.delete<ApiResult>(`/admin/courses/content/${contentId}`)
    return response.data
  },

  async reorderCourseContent(
    courseId: number,
    orderedIds: number[],
    parentId: number | null
  ): Promise<ApiResult> {
    const response = await apiClient.put<ApiResult>(
      `/admin/courses/${courseId}/content/reorder`,
      { orderedIds, parentId },
      { timeout: 60000 }
    )
    return response.data
  },

  async listVideos(params?: {
    q?: string
    page?: number
    limit?: number
    includeInactive?: boolean
  }) {
    const query = new URLSearchParams()
    if (params?.q) query.set('q', params.q)
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.includeInactive) query.set('includeInactive', '1')
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await apiClient.get<{
      success: boolean
      data: {
        items: AdminVideo[]
        pagination: Pagination
        limits?: { maxBytes: number; maxChunkBytes: number }
      }
    }>(`/admin/videos${suffix}`)
    return response.data
  },

  async getVideo(id: number) {
    const response = await apiClient.get<{ success: boolean; data: AdminVideo; message?: string }>(
      `/admin/videos/${id}`
    )
    return response.data
  },

  async getVideoUsages(id: number) {
    const response = await apiClient.get<{
      success: boolean
      data: {
        videoId: number
        videoTitle: string
        usageCount: number
        usages: AdminVideoUsage[]
      }
      message?: string
    }>(`/admin/videos/${id}/usages`)
    return response.data
  },

  async updateVideo(
    id: number,
    data: { title?: string; description?: string | null; duration?: number; isActive?: number }
  ) {
    const response = await apiClient.put<{ success: boolean; data: AdminVideo; message?: string }>(
      `/admin/videos/${id}`,
      data
    )
    return response.data
  },

  async deleteVideo(id: number, options?: { hard?: boolean }) {
    const suffix = options?.hard ? '?hard=1' : ''
    const response = await apiClient.delete<{
      success: boolean
      message?: string
      data?: { usageCount?: number; usages?: AdminVideoUsage[] }
    }>(`/admin/videos/${id}${suffix}`)
    return response.data
  },

  async uploadVideo(file: File, title?: string) {
    // Prefer chunked for reliability; keep single-shot for tiny files
    if (file.size > 20 * 1024 * 1024) {
      return AdminApi.uploadVideoChunked(file, { title })
    }
    const formData = new FormData()
    formData.append('file', file)
    if (title) formData.append('title', title)
    const response = await apiClient.post<{ success: boolean; data: AdminVideo; message?: string }>(
      '/admin/videos/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      }
    )
    return response.data
  },

  async uploadVideoChunked(
    file: File,
    options?: {
      title?: string
      description?: string
      duration?: number
      onProgress?: (progress: AdminVideoUploadProgress) => void
      signal?: AbortSignal
    }
  ) {
    if (file.size <= 0) {
      throw new Error('File rỗng')
    }
    if (file.size > ADMIN_VIDEO_MAX_BYTES) {
      throw new Error(`File vượt quá giới hạn 1.5GB (${ADMIN_VIDEO_MAX_BYTES} bytes)`)
    }

    const chunkSize = ADMIN_VIDEO_CHUNK_BYTES
    const totalChunks = Math.ceil(file.size / chunkSize)
    const onProgress = options?.onProgress
    const signal = options?.signal

    onProgress?.({
      phase: 'init',
      percent: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
      totalChunks,
    })

    const initRes = await apiClient.post<{
      success: boolean
      data: { uploadId: string; maxChunkBytes: number; totalChunks: number }
      message?: string
    }>(
      '/admin/videos/upload/init',
      {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'video/mp4',
        title: options?.title,
        description: options?.description,
        duration: options?.duration,
        totalChunks,
      },
      { timeout: 60000, signal }
    )

    if (!initRes.data.success || !initRes.data.data?.uploadId) {
      throw new Error(initRes.data.message || 'Không khởi tạo được phiên upload')
    }

    const uploadId = initRes.data.data.uploadId
    let uploadedBytes = 0

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        if (signal?.aborted) {
          throw new DOMException('Upload aborted', 'AbortError')
        }
        const start = chunkIndex * chunkSize
        const end = Math.min(start + chunkSize, file.size)
        const blob = file.slice(start, end)
        const formData = new FormData()
        formData.append('chunk', blob, `chunk_${chunkIndex}`)
        formData.append('chunkIndex', String(chunkIndex))

        let attempt = 0
        let ok = false
        while (attempt < 3 && !ok) {
          attempt += 1
          try {
            await apiClient.put(`/admin/videos/upload/${uploadId}/chunk`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 120000,
              signal,
            })
            ok = true
          } catch (err) {
            if (signal?.aborted) throw err
            if (attempt >= 3) throw err
            await new Promise((r) => setTimeout(r, 500 * attempt))
          }
        }

        uploadedBytes = end
        onProgress?.({
          phase: 'uploading',
          percent: Math.min(99, Math.round((uploadedBytes / file.size) * 100)),
          uploadedBytes,
          totalBytes: file.size,
          chunkIndex,
          totalChunks,
        })
      }

      onProgress?.({
        phase: 'completing',
        percent: 99,
        uploadedBytes: file.size,
        totalBytes: file.size,
        totalChunks,
      })

      const completeRes = await apiClient.post<{
        success: boolean
        data: AdminVideo
        message?: string
      }>(
        `/admin/videos/upload/${uploadId}/complete`,
        {
          title: options?.title,
          description: options?.description,
          duration: options?.duration,
        },
        { timeout: 300000, signal }
      )

      if (!completeRes.data.success || !completeRes.data.data) {
        throw new Error(completeRes.data.message || 'Hoàn tất upload thất bại')
      }

      onProgress?.({
        phase: 'done',
        percent: 100,
        uploadedBytes: file.size,
        totalBytes: file.size,
        totalChunks,
      })

      return completeRes.data
    } catch (err) {
      try {
        await apiClient.delete(`/admin/videos/upload/${uploadId}`, { timeout: 15000 })
      } catch {
        /* ignore abort cleanup errors */
      }
      throw err
    }
  },

  async listTestPapers(params?: {
    q?: string
    objectId?: number
    isActive?: number
    page?: number
    limit?: number
  }) {
    const query = new URLSearchParams()
    if (params?.q) query.set('q', params.q)
    if (params?.objectId != null) query.set('objectId', String(params.objectId))
    if (params?.isActive != null) query.set('isActive', String(params.isActive))
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await apiClient.get<{
      success: boolean
      data: { items: AdminTestPaper[]; pagination: Pagination }
    }>(`/admin/tests${suffix}`)
    return response.data
  },

  async listTestObjects() {
    const response = await apiClient.get<{ success: boolean; data: AdminTestObject[] }>('/admin/test-objects')
    return response.data
  },

  async listQuestionTopics() {
    const response = await apiClient.get<{ success: boolean; data: AdminQuestionTopic[] }>(
      '/admin/question-topics'
    )
    return response.data
  },

  async createQuestionTopic(payload: {
    name: string
    description?: string | null
    parentId?: number | null
    isActive?: number
  }) {
    const response = await apiClient.post<{
      success: boolean
      data: AdminQuestionTopic
      message?: string
    }>('/admin/question-topics', payload)
    return response.data
  },

  async updateQuestionTopic(
    id: number,
    payload: {
      name?: string
      description?: string | null
      parentId?: number | null
      isActive?: number
    }
  ) {
    const response = await apiClient.put<{
      success: boolean
      data: AdminQuestionTopic
      message?: string
    }>(`/admin/question-topics/${id}`, payload)
    return response.data
  },

  async listQuestionGroups(params?: {
    q?: string
    isActive?: number
    page?: number
    limit?: number
  }) {
    const query = new URLSearchParams()
    if (params?.q) query.set('q', params.q)
    if (params?.isActive != null) query.set('isActive', String(params.isActive))
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await apiClient.get<{
      success: boolean
      data: { items: AdminQuestionGroup[]; pagination: Pagination }
    }>(`/admin/question-groups${suffix}`)
    return response.data
  },

  async getQuestionGroup(id: number) {
    const response = await apiClient.get<{ success: boolean; data: AdminQuestionGroup }>(
      `/admin/question-groups/${id}`
    )
    return response.data
  },

  async createQuestionGroup(payload: { name: string; isActive?: number }) {
    const response = await apiClient.post<{
      success: boolean
      data: AdminQuestionGroup
      message?: string
    }>('/admin/question-groups', payload)
    return response.data
  },

  async updateQuestionGroup(id: number, payload: { name?: string; isActive?: number }) {
    const response = await apiClient.put<{
      success: boolean
      data: AdminQuestionGroup
      message?: string
    }>(`/admin/question-groups/${id}`, payload)
    return response.data
  },

  async listQuestions(params?: {
    q?: string
    topicId?: number
    groupId?: number
    questionType?: string
    level?: string
    isActive?: number
    excludeIds?: number[]
    ungrouped?: boolean
    page?: number
    limit?: number
  }) {
    const query = new URLSearchParams()
    if (params?.q) query.set('q', params.q)
    if (params?.topicId != null) query.set('topicId', String(params.topicId))
    if (params?.groupId != null) query.set('groupId', String(params.groupId))
    if (params?.questionType) query.set('questionType', params.questionType)
    if (params?.level) query.set('level', params.level)
    if (params?.isActive != null) query.set('isActive', String(params.isActive))
    if (params?.excludeIds?.length) query.set('excludeIds', params.excludeIds.join(','))
    if (params?.ungrouped) query.set('ungrouped', '1')
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await apiClient.get<{
      success: boolean
      data: { items: AdminQuestionListItem[]; pagination: Pagination }
    }>(`/admin/questions${suffix}`)
    return response.data
  },

  async getQuestion(id: number) {
    const response = await apiClient.get<{ success: boolean; data: AdminQuestionListItem }>(
      `/admin/questions/${id}`
    )
    return response.data
  },

  async createQuestion(payload: AdminQuestionUpsertPayload) {
    const response = await apiClient.post<{
      success: boolean
      data: AdminQuestionListItem
      message?: string
    }>('/admin/questions', payload)
    return response.data
  },

  async updateQuestion(id: number, payload: Partial<AdminQuestionUpsertPayload>) {
    const response = await apiClient.put<{
      success: boolean
      data: AdminQuestionListItem
      message?: string
    }>(`/admin/questions/${id}`, payload)
    return response.data
  },

  async setQuestionActive(id: number, isActive: number) {
    const response = await apiClient.patch<{
      success: boolean
      data: AdminQuestionListItem
      message?: string
    }>(`/admin/questions/${id}/active`, { isActive })
    return response.data
  },

  async createTestPaper(payload: AdminTestPaperUpsertPayload) {
    const response = await apiClient.post<{
      success: boolean
      data: AdminTestPaper
      message?: string
    }>('/admin/tests', payload)
    return response.data
  },

  async updateTestPaper(paperId: number, payload: Partial<AdminTestPaperUpsertPayload>) {
    const response = await apiClient.put<{
      success: boolean
      data: AdminTestPaper
      message?: string
    }>(`/admin/tests/${paperId}`, payload)
    return response.data
  },

  async setTestPaperQuestions(paperId: number, questions: AdminTestPaperQuestionInput[]) {
    const response = await apiClient.put<{
      success: boolean
      data: {
        paper: AdminTestPaper
        questions: Array<{
          id: number
          paperId: number
          questionId: number
          groupId?: number | null
          point: number
          sort: number
        }>
      }
      message?: string
    }>(`/admin/tests/${paperId}/questions`, { questions })
    return response.data
  },

  async setTestPaperActive(paperId: number, isActive: number) {
    const response = await apiClient.patch<{
      success: boolean
      data: AdminTestPaper
      message?: string
    }>(`/admin/tests/${paperId}/active`, { isActive })
    return response.data
  },

  async uploadImage(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<{ success: boolean; data: { url: string }; message?: string }>(
      '/admin/uploads/image',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      }
    )
    return response.data
  },

  async createEnterpriseBatch(payload: {
    taxCode: string
    enterpriseName: string
    invoiceEmail: string
    address: string
    invoiceMode?: 'NOW' | 'LATER'
    rows: Array<{ fullName: string; email: string; phone?: string; classroomId: number; fee?: number; sourceRowIndex?: number }>
  }): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>('/admin/enterprise-registrations/batches', payload)
    return response.data
  },

  async getEnterpriseBatches(params?: { page?: number; limit?: number; search?: string }): Promise<{
    success: boolean
    data: { items: AdminEnterpriseBatchItem[]; pagination: Pagination }
  }> {
    const query = new URLSearchParams()
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.search) query.set('search', params.search)
    const response = await apiClient.get<{
      success: boolean
      data: { items: AdminEnterpriseBatchItem[]; pagination: Pagination }
    }>(`/admin/enterprise-registrations/batches?${query.toString()}`)
    return response.data
  },

  async getEnterpriseBatchDetail(batchId: number): Promise<{
    success: boolean
    data: AdminEnterpriseBatchDetail
  }> {
    const response = await apiClient.get<{
      success: boolean
      data: AdminEnterpriseBatchDetail
    }>(`/admin/enterprise-registrations/batches/${batchId}`)
    return response.data
  },

  async publishMisaForEnterpriseBatch(batchId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/enterprise-registrations/batches/${batchId}/publish-misa-invoice`)
    return response.data
  },

  async publishMisaForRegistration(registrationId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/registrations/${registrationId}/publish-misa-invoice`)
    return response.data
  },

  async confirmRegistrationFulfillment(registrationId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/registrations/${registrationId}/confirm-fulfillment`)
    return response.data
  },

  async cancelWaitPaymentRegistration(registrationId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/registrations/${registrationId}/cancel-wait-payment`)
    return response.data
  },

  async getMisaLogs(params: { page?: number; limit?: number; status?: 'PENDING' | 'SUCCESS' | 'FAIL' }) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.status) query.set('status', params.status)
    const response = await apiClient.get<{ success: boolean; data: { items: AdminMisaInvoiceLog[]; pagination: Pagination } }>(
      `/admin/misa-invoice-logs?${query.toString()}`
    )
    return response.data
  },

  async retryMisaLog(logId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/misa-invoice-logs/${logId}/retry`)
    return response.data
  },

  async getSecurityWarnings(params: { page?: number; limit?: number } = {}) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    const response = await apiClient.get<{
      success: boolean
      data: {
        items: AdminSecurityWarningItem[]
        pagination: Pagination
        unresolvedCount: number
      }
    }>(`/admin/security-warnings?${query.toString()}`)
    return response.data
  },

  async getSecurityWarningDetail(userId: number) {
    const response = await apiClient.get<{
      success: boolean
      data: AdminSecurityWarningDetail
    }>(`/admin/security-warnings/${userId}`)
    return response.data
  },

  async listWatchlist(params: { page?: number; limit?: number } = {}) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    const response = await apiClient.get<{
      success: boolean
      data: {
        items: AdminWatchlistItem[]
        pagination: Pagination
      }
    }>(`/admin/watchlist?${query.toString()}`)
    return response.data
  },

  async addToWatchlist(userId: number, payload: { reason?: string; note?: string } = {}) {
    const response = await apiClient.post<ApiResult>(`/admin/watchlist/${userId}`, payload)
    return response.data
  },

  async removeFromWatchlist(userId: number) {
    const response = await apiClient.delete<ApiResult>(`/admin/watchlist/${userId}`)
    return response.data
  },

  async unlockUser(userId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/users/${userId}/unlock`)
    return response.data
  },

  async lockUser(userId: number, reason?: string): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/users/${userId}/lock`, { reason })
    return response.data
  },

  async listUsers(params: AdminUserFilter = {}): Promise<AdminUsersListResponse> {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.q) query.set('q', params.q)
    if (params.role) query.set('role', params.role)
    if (params.status) query.set('status', params.status)
    const response = await apiClient.get<AdminUsersListResponse>(`/users?${query.toString()}`)
    return response.data
  },

  async getUser(userId: number) {
    const response = await apiClient.get<{ success: boolean; data: AdminUserItem; message?: string }>(
      `/users/${userId}`
    )
    return response.data
  },

  async updateUser(userId: number, payload: AdminUserUpdatePayload) {
    const response = await apiClient.put<{
      success: boolean
      data: AdminUserItem
      message?: string
    }>(`/users/${userId}`, payload)
    return response.data
  },

  async revokeUserDevice(userId: number, deviceRowId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(
      `/admin/users/${userId}/devices/${deviceRowId}/revoke`
    )
    return response.data
  },

  async revokeAllUserDevices(userId: number): Promise<ApiResult> {
    const response = await apiClient.post<ApiResult>(`/admin/users/${userId}/devices/revoke-all`)
    return response.data
  },

  async getSurveys(filters: AdminSurveyFilter = {}): Promise<AdminSurveysResponse> {
    const params = new URLSearchParams()
    if (filters.course_id) params.set('course_id', String(filters.course_id))
    if (filters.status) params.set('status', filters.status)
    if (filters.page) params.set('page', String(filters.page))
    if (filters.limit) params.set('limit', String(filters.limit))
    const response = await apiClient.get<AdminSurveysResponse>(`/admin/surveys?${params.toString()}`)
    return response.data
  },

  async setSurveyVisibility(
    surveyId: number,
    hidden: boolean,
    note?: string
  ): Promise<ApiResult & { data?: { id: number; status: string; isHidden: boolean } }> {
    const response = await apiClient.patch<
      ApiResult & { data?: { id: number; status: string; isHidden: boolean } }
    >(`/admin/surveys/${surveyId}/visibility`, { hidden, note })
    return response.data
  },

  async listTestResults(filters: {
    courseId?: number
    paperId?: number
    gradingStatus?: AdminTestResultGradingStatus
    q?: string
    page?: number
    limit?: number
  } = {}): Promise<AdminTestResultListResponse> {
    const params = new URLSearchParams()
    if (filters.courseId != null) params.set('courseId', String(filters.courseId))
    if (filters.paperId != null) params.set('paperId', String(filters.paperId))
    if (filters.gradingStatus) params.set('gradingStatus', filters.gradingStatus)
    if (filters.q) params.set('q', filters.q)
    if (filters.page) params.set('page', String(filters.page))
    if (filters.limit) params.set('limit', String(filters.limit))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await apiClient.get<AdminTestResultListResponse>(`/admin/test-results${suffix}`)
    return response.data
  },

  async getTestResult(
    resultId: number
  ): Promise<{ success: boolean; data: AdminTestResultDetail; message?: string }> {
    const response = await apiClient.get<{ success: boolean; data: AdminTestResultDetail; message?: string }>(
      `/admin/test-results/${resultId}`
    )
    return response.data
  },

  async gradeTestResult(
    resultId: number,
    payload: AdminTestResultGradePayload
  ): Promise<ApiResult & { data?: AdminTestResultDetail }> {
    const response = await apiClient.put<ApiResult & { data?: AdminTestResultDetail }>(
      `/admin/test-results/${resultId}/grade`,
      payload
    )
    return response.data
  },

  async getBlogPostsMeta() {
    const response = await apiClient.get<{
      success: boolean
      data: {
        topics: AdminBlogTopicOption[]
        sharingTypes: AdminBlogSharingTypeOption[]
        statuses: AdminBlogPostStatus[]
      }
    }>('/admin/posts/meta')
    return response.data
  },

  async listBlogPosts(filters: AdminBlogPostFilter = {}): Promise<AdminBlogPostsListResponse> {
    const params = new URLSearchParams()
    if (filters.page) params.set('page', String(filters.page))
    if (filters.limit) params.set('limit', String(filters.limit))
    if (filters.search) params.set('search', filters.search)
    if (filters.topic_id) params.set('topic_id', String(filters.topic_id))
    if (filters.sharing_type_id) params.set('sharing_type_id', String(filters.sharing_type_id))
    if (filters.status) params.set('status', filters.status)
    if (filters.include_deleted) params.set('include_deleted', '1')
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await apiClient.get<AdminBlogPostsListResponse>(`/admin/posts${suffix}`)
    return response.data
  },

  async getBlogPost(id: number) {
    const response = await apiClient.get<{ success: boolean; data: AdminBlogPost; message?: string }>(
      `/admin/posts/${id}`
    )
    return response.data
  },

  async createBlogPost(payload: AdminBlogPostPayload) {
    const response = await apiClient.post<{ success: boolean; data: AdminBlogPost; message?: string }>(
      '/admin/posts',
      payload
    )
    return response.data
  },

  async updateBlogPost(id: number, payload: Partial<AdminBlogPostPayload>) {
    const response = await apiClient.put<{ success: boolean; data: AdminBlogPost; message?: string }>(
      `/admin/posts/${id}`,
      payload
    )
    return response.data
  },

  async deleteBlogPost(id: number) {
    const response = await apiClient.delete<ApiResult & { data?: { id: number } }>(`/admin/posts/${id}`)
    return response.data
  },

  async getWorkshopRegistrations(params: {
    page?: number
    limit?: number
    search?: string
    workshop_id?: string
  }): Promise<{
    success: boolean
    data: { items: AdminWorkshopRegistrationItem[]; pagination: Pagination }
  }> {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.search) query.set('search', params.search)
    if (params.workshop_id) query.set('workshop_id', params.workshop_id)
    const response = await apiClient.get<{
      success: boolean
      data: { items: AdminWorkshopRegistrationItem[]; pagination: Pagination }
    }>(`/admin/workshop-registrations?${query.toString()}`)
    return response.data
  },

  async sendWorkshopMeetLink(params: {
    meet_link: string
    workshop_id?: string
    force?: boolean
    registration_ids?: number[]
  }): Promise<{ success: boolean; message: string; total?: number }> {
    const response = await apiClient.post<{ success: boolean; message: string; total?: number }>(
      '/admin/workshop-registrations/send-meet-link',
      params
    )
    return response.data
  },

  async getWorkshopEmailLogs(workshopId?: string): Promise<WorkshopEmailLogsResponse> {
    const query = workshopId ? `?workshop_id=${workshopId}` : ''
    const response = await apiClient.get<WorkshopEmailLogsResponse>(
      `/admin/workshop-registrations/email-logs${query}`
    )
    return response.data
  },

  async retryFailedWorkshopEmails(params: {
    workshop_id?: string
    log_ids?: number[]
  }): Promise<{ success: boolean; message: string; retried?: number }> {
    const response = await apiClient.post<{ success: boolean; message: string; retried?: number }>(
      '/admin/workshop-registrations/retry-failed',
      params
    )
    return response.data
  },

  async getWorkshopConfig(workshopId: string): Promise<WorkshopConfigResponse> {
    const response = await apiClient.get<WorkshopConfigResponse>(`/admin/workshops/${workshopId}`)
    return response.data
  },

  async updateWorkshopConfig(
    workshopId: string,
    payload: { youtube_url?: string | null; meet_link?: string | null }
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/admin/workshops/${workshopId}`,
      payload
    )
    return response.data
  },
}

export type AdminSecurityWarningItem = {
  id: number
  email: string
  fullName: string
  status: string
  lockReason?: string | null
  lockedAt?: string | null
  openEventCount: number
  lastEventType?: string | null
  lastEventAt?: string | null
}

export type AdminUserItem = {
  id: number
  email: string
  fullName: string
  firstName: string
  phone?: string
  cccd?: string | null
  address?: string | null
  status: 'ACTIVE' | 'INACTIVE' | 'BANNED' | string
  roles: string[]
  isActive: boolean
  catJobId?: number | null
  lockedAt?: string | null
  lockReason?: string | null
  createdAt: string
  updatedAt: string
}

export type AdminUserFilter = {
  q?: string
  role?: string
  status?: string
  page?: number
  limit?: number
}

export type AdminUserUpdatePayload = {
  email?: string
  fullName?: string
  firstName?: string
  phone?: string
  cccd?: string | null
  address?: string | null
  roles?: string[]
  status?: string
  catJobId?: number | null
}

export type AdminUsersListResponse = {
  success: boolean
  message: string
  data: { items: AdminUserItem[]; pagination: Pagination }
}

export type AdminSecurityWarningDetail = {
  user: {
    id: number
    email: string
    fullName: string
    status: string
    lockReason?: string | null
    lockedAt?: string | null
  }
  events: Array<{
    id: number
    eventType: string
    severity: string
    payload?: Record<string, unknown> | null
    createdAt: string
    resolvedAt?: string | null
    resolvedBy?: number | null
  }>
  devices: Array<{
    id: number
    deviceId: string
    deviceLabel?: string | null
    lastIp?: string | null
    userAgent?: string | null
    lastSeenAt: string
    createdAt: string
    revokedAt?: string | null
  }>
  watchlisted?: boolean
  watchlist?: {
    id: number
    userId: number
    reason?: string | null
    note?: string | null
    addedBy?: number | null
    createdAt: string
  } | null
  learningActivities?: AdminLearningActivityItem[]
}

export type AdminWatchlistItem = {
  id: number
  userId: number
  reason?: string | null
  note?: string | null
  addedBy?: number | null
  createdAt: string
  email: string
  fullName: string
  status: string
  lockReason?: string | null
  lockedAt?: string | null
  lastActivityAt?: string | null
}

export type AdminLearningActivityItem = {
  id: number
  eventType: string
  courseId?: number | null
  contentId?: number | null
  resourceType?: string | null
  resourceId?: number | null
  deviceId?: string | null
  ip?: string | null
  userAgent?: string | null
  payload?: Record<string, unknown> | null
  createdAt: string
}

export type AdminBlogPostStatus = 'ACTIVE' | 'INACTIVE'

export type AdminBlogTopicOption = {
  id: number
  title: string
  slug?: string | null
  description?: string | null
}

export type AdminBlogSharingTypeOption = {
  id: number
  type: string
}

export type AdminBlogPost = {
  id: number
  uuid: string
  sharingTypeId: number
  sharingTypeName?: string | null
  topicId: number
  topicTitle?: string | null
  topicSlug?: string | null
  author?: string | null
  title: string
  status: AdminBlogPostStatus | string
  shortDescription?: string | null
  avatarUrl?: string | null
  postContent?: string | null
  updatedBy?: string | null
  totalView: number
  totalLike: number
  totalShare: number
  isFeatured: boolean
  slug?: string | null
  metaTitle?: string | null
  metaDescription?: string | null
  openGraphImageUrl?: string | null
  tags?: string | null
  tagNames?: string[]
  relatedCourseQuote?: string | null
  relatedCourseId1?: number | null
  relatedCourseId2?: number | null
  relatedCourses?: AdminBlogRelatedCourse[]
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

export type AdminBlogRelatedCourse = {
  id: number
  hashId: string
  code: string
  name: string
  shortDescription?: string | null
  fee?: number | null
  imageUrl?: string | null
  method?: string | null
}

export type AdminBlogPostFilter = {
  page?: number
  limit?: number
  search?: string
  topic_id?: number
  sharing_type_id?: number
  status?: AdminBlogPostStatus
  include_deleted?: boolean
}

export type AdminBlogPostsListResponse = {
  success: boolean
  message?: string
  data: {
    items: AdminBlogPost[]
    pagination: Pagination
  }
}

/** Không gồm gắn bài test vào khóa học — chỉ CTA đăng ký khóa liên quan */
export type AdminBlogPostPayload = {
  title: string
  topicId: number
  sharingTypeId: number
  postContent?: string | null
  shortDescription?: string | null
  avatarUrl?: string | null
  author?: string | null
  status?: AdminBlogPostStatus
  slug?: string | null
  uuid?: string | null
  isFeatured?: boolean
  metaTitle?: string | null
  metaDescription?: string | null
  openGraphImageUrl?: string | null
  tags?: string | null
  relatedCourseQuote?: string | null
  relatedCourseId1?: number | null
  relatedCourseId2?: number | null
}
